
import { americanOddsToImpliedProbability } from "@/lib/odds";

export function impliedProbability(american: number): number {
  const p = americanOddsToImpliedProbability(american);
  return p == null ? NaN : p;
}

function probabilityToAmericanRaw(probability: number): number {
  if (probability >= 0.5) {
    return -(probability * 100) / (1 - probability);
  }
  return (100 * (1 - probability)) / probability;
}

function roundToStep(value: number, step: number): number {
  return Math.round(value / step) * step;
}

export interface OddsTick {
  american: number;
  prob: number;
}

const ROUND_STEP = 5;

export function roundOddsTicks(
  probMin: number,
  probMax: number,
  target = 5,
): OddsTick[] {
  if (!Number.isFinite(probMin) || !Number.isFinite(probMax)) {
    return [];
  }
  const lo = Math.min(probMin, probMax);
  const hi = Math.max(probMin, probMax);
  const count = Math.max(2, Math.floor(target));
  const seen = new Set<number>();
  const ticks: OddsTick[] = [];

  for (let i = 0; i < count; i += 1) {
    const p = lo === hi ? lo : lo + ((hi - lo) * i) / (count - 1);
    const american = roundToStep(probabilityToAmericanRaw(p), ROUND_STEP);
    if (american === 0 || seen.has(american)) {
      continue;
    }
    const prob = americanOddsToImpliedProbability(american);
    if (prob == null) {
      continue;
    }
    seen.add(american);
    ticks.push({ american, prob });
    if (lo === hi) {
      break;
    }
  }

  return ticks.sort((a, b) => a.prob - b.prob);
}

export function projectX(
  value: number,
  min: number,
  max: number,
  rangeStart: number,
  rangeEnd: number,
): number {
  return rangeStart + (rangeEnd - rangeStart) * fraction(value, min, max);
}

export function projectY(
  value: number,
  min: number,
  max: number,
  top: number,
  bottom: number,
): number {
  return top + (bottom - top) * (1 - fraction(value, min, max));
}

export interface ProbabilityReferenceLine {
  probability: number;
  label: string;
}

export function robustProbabilityDomain(
  values: readonly number[],
  alwaysInclude: readonly number[] = [],
): { pMin: number; pMax: number } {
  const finite = values.filter((value) => Number.isFinite(value));
  const extras = alwaysInclude.filter((value) => Number.isFinite(value));
  const pool = finite.length > 0 ? finite : extras;
  if (pool.length === 0) {
    return { pMin: 0.45, pMax: 0.55 };
  }

  let lo: number;
  let hi: number;
  if (finite.length >= 8) {
    const sorted = [...finite].sort((a, b) => a - b);
    const last = sorted.length - 1;
    const q1 = sorted[Math.floor(last * 0.25)]!;
    const q3 = sorted[Math.ceil(last * 0.75)]!;
    const fence = Math.max((q3 - q1) * 3, 0.01);
    const inner = sorted.filter((value) => value >= q1 - fence && value <= q3 + fence);
    lo = Math.min(...inner);
    hi = Math.max(...inner);
  } else {
    lo = Math.min(...pool);
    hi = Math.max(...pool);
  }
  for (const extra of extras) {
    lo = Math.min(lo, extra);
    hi = Math.max(hi, extra);
  }
  const pad = (hi - lo) * 0.12 || 0.01;
  return { pMin: lo - pad, pMax: hi + pad };
}

function fraction(value: number, min: number, max: number): number {
  const span = max - min;
  return span === 0 ? 0 : (value - min) / span;
}

export function formatAmericanOdds(american: number): string {
  const rounded = Math.round(american);
  return rounded > 0 ? `+${rounded}` : `${rounded}`;
}
